import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Sun, LayoutDashboard, Waves, Users, Database, Wrench,
  Kanban, XCircle, Upload, UsersRound, Send, HardHat, FileText,
  CalendarPlus, CornerDownLeft, MapPin, BarChart3, Gavel,
} from 'lucide-react';
import { globalSearch } from '../utils/api';
import { navFor } from '../utils/roleAccess';

/**
 * Global command palette — Ctrl/Cmd+K anywhere.
 * Jump to a page, find a contractor or letting, or fire a quick action.
 *
 * Every page carries the same `nav` key its Sidebar section uses, and the list
 * is filtered through navFor() for exactly one reason: the palette must not
 * offer a door the sidebar deliberately left off. An assistant who never sees
 * "Bid Intelligence" in the nav shouldn't find it by typing Ctrl+K.
 *
 * Like roleAccess.js itself, this is decluttering, NOT a security boundary —
 * these routes still render if typed into the URL bar. The one role that is a
 * boundary (producer) never reaches this component: App.js renders the palette
 * only on the non-producer branch, and server/middleware/producerScope.js
 * enforces the same line for real.
 */

const PAGES = [
  // The Command Center's default tab, so it rides that section's nav key —
  // same treatment as Reports below.
  { nav: 'dashboard', label: 'Today', to: '/dashboard', icon: Sun, keywords: 'home actions follow-ups morning priorities queue' },
  { nav: 'south-florida', label: 'South Florida', to: '/south-florida', icon: Waves, keywords: 'producer territory tri-county miami dade broward palm beach' },
  { nav: 'dashboard', label: 'Command Center', to: '/dashboard?tab=overview', icon: LayoutDashboard, keywords: 'dashboard stats overview map hot leads activity' },
  // A tab inside the Command Center, so it rides that section's nav key.
  { nav: 'dashboard', label: 'Reports', to: '/dashboard?tab=reports', icon: BarChart3, keywords: 'outcomes conversion funnel lead source roi analytics' },
  { nav: 'bid-intel', label: 'Bid Results', to: '/bid-results?preset=all', icon: Gavel, keywords: 'awards low bidder tabulation results winners apparent low national all sources' },
  { nav: 'contractors', label: 'Contractors', to: '/contractors', icon: Users, keywords: 'companies leads list prospects hot' },
  { nav: 'map', label: 'Contractor Map', to: '/map', icon: MapPin, keywords: 'locations territory geography where' },
  { nav: 'pipeline', label: 'Pipeline', to: '/pipeline', icon: Kanban, keywords: 'deals stages board kanban' },
  { nav: 'workspace', label: 'Outreach Workspace', to: '/workspace', icon: Send, keywords: 'assistant queue mailer calls' },
  { nav: 'bid-intel', label: 'Rejected & Lost Bids', to: '/bid-results?mode=bidders&bid_status=rejected,non-responsive&preset=rejected', icon: XCircle, keywords: 'irregular withdrawn lost losers rejected non-responsive did not win' },
  { nav: 'tools', label: 'Bond Tools', to: '/bond-tools', icon: Wrench, keywords: 'calculator letters wip financial' },
  // The whole Admin & Data section is admin-only in the Sidebar, so all three
  // are here too — the palette must not offer a door the sidebar left off.
  { nav: 'admin', label: 'Data Sources', to: '/data-sources', icon: Database, keywords: 'scrapers imports admin', adminOnly: true },
  { nav: 'admin', label: 'Import CSV', to: '/import-contractors', icon: Upload, keywords: 'upload bulk', adminOnly: true },
  { nav: 'admin', label: 'Team', to: '/team', icon: UsersRound, keywords: 'users admin members', adminOnly: true },
];

export default function CommandPalette({ role }) {
  const isAdmin = role === 'admin';
  const nav = useMemo(() => navFor({ role }), [role]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ contractors: [], lettings: [] });
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Global hotkey
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Expose an imperative opener so the sidebar search button can trigger it
  useEffect(() => {
    const opener = () => setOpen(true);
    window.addEventListener('bondhq:open-palette', opener);
    return () => window.removeEventListener('bondhq:open-palette', opener);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults({ contractors: [], lettings: [] });
      setSelected(0);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 10);
    }
  }, [open]);

  // Debounced contractor/letting search
  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setResults({ contractors: [], lettings: [] });
      return;
    }
    const t = setTimeout(() => {
      globalSearch(query.trim())
        .then((r) => setResults({ contractors: r.contractors || [], lettings: r.lettings || [] }))
        .catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [query, open]);

  const q = query.trim().toLowerCase();
  const items = useMemo(() => {
    const pageItems = PAGES
      .filter((p) => nav.has(p.nav))
      .filter((p) => !p.adminOnly || isAdmin)
      .filter((p) => !q || p.label.toLowerCase().includes(q) || p.keywords.includes(q))
      .map((p) => ({ type: 'page', ...p }));
    const contractorItems = results.contractors.slice(0, 6).map((c) => ({
      type: 'contractor', label: c.name, sub: [c.city, c.state].filter(Boolean).join(', '),
      icon: HardHat, to: `/contractors/${c.id}`, id: c.id,
    }));
    const lettingItems = results.lettings.slice(0, 4).map((l) => ({
      type: 'letting', label: l.project_id || l.description || `Letting ${l.id}`,
      sub: [l.county, l.state].filter(Boolean).join(', '),
      icon: FileText, to: `/lettings/${l.id}`,
    }));
    const actionItems = contractorItems.length > 0 && q
      ? [{
          type: 'action', label: `Create follow-up for “${results.contractors[0].name}”`,
          icon: CalendarPlus, to: `/contractors/${results.contractors[0].id}?action=follow-up`,
        }]
      : [];
    return [...contractorItems, ...pageItems, ...lettingItems, ...actionItems];
  }, [q, results, isAdmin, nav]);

  useEffect(() => { setSelected(0); }, [items.length, q]);

  const run = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    navigate(item.to);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected((s) => Math.min(s + 1, items.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); run(items[selected]); }
  };

  // Keep the selected item visible
  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector('.cmdk-item.selected');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  if (!open) return null;

  const groups = [
    { title: 'Contractors', items: items.filter((i) => i.type === 'contractor') },
    { title: 'Pages', items: items.filter((i) => i.type === 'page') },
    { title: 'Lettings', items: items.filter((i) => i.type === 'letting') },
    { title: 'Actions', items: items.filter((i) => i.type === 'action') },
  ].filter((g) => g.items.length > 0);

  return (
    <div className="cmdk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div className="cmdk-panel" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="cmdk-input-wrap">
          <Search size={17} aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search contractors, jump to a page…"
            aria-label="Search"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-listbox"
            aria-autocomplete="list"
            aria-activedescendant={items[selected] ? `cmdk-opt-${selected}` : undefined}
          />
          <span className="kbd-chip">esc</span>
        </div>
        <div className="cmdk-results" ref={listRef} id="cmdk-listbox" role="listbox" aria-label="Results">
          {groups.length === 0 && (
            <div style={{ padding: '20px 16px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
              {q.length >= 2 ? 'No matches. Try a different name.' : 'Type to search contractors and pages.'}
            </div>
          )}
          {groups.map((g) => (
            <div key={g.title} role="group" aria-label={g.title}>
              <div className="cmdk-group-title" aria-hidden="true">{g.title}</div>
              {g.items.map((item) => {
                const idx = items.indexOf(item);
                const Icon = item.icon;
                return (
                  <div
                    key={`${item.type}-${item.to}-${item.label}`}
                    id={`cmdk-opt-${idx}`}
                    role="option"
                    aria-selected={idx === selected}
                    className={`cmdk-item${idx === selected ? ' selected' : ''}`}
                    onMouseEnter={() => setSelected(idx)}
                    onMouseDown={(e) => { e.preventDefault(); run(item); }}
                  >
                    <Icon size={15} strokeWidth={2.2} aria-hidden="true" />
                    <span className="truncate">{item.label}</span>
                    {item.sub && <span className="cmdk-item-sub">{item.sub}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="cmdk-footer">
          <span><kbd>↑</kbd> <kbd>↓</kbd> navigate</span>
          <span><CornerDownLeft size={10} style={{ verticalAlign: -1 }} /> <kbd>enter</kbd> open</span>
          <span><kbd>ctrl</kbd>+<kbd>k</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
}
